<script lang="ts" setup>
import { ref, onMounted, onUnmounted, useSlots, nextTick } from 'vue';
import { useWindowScroll, watchThrottled, useResizeObserver, onClickOutside } from '@vueuse/core';
import scrollama from 'scrollama';

const contentEl = ref();
const lineEl = ref();
const navigatorEl = ref<HTMLElement>();
const listEl = ref<HTMLUListElement>();
const dropdownRef = ref<HTMLElement>();
const fixed = ref(false);
const activeIndex = ref(0);
const dropdownOpen = ref(false);

const scroller = scrollama();
const slots = useSlots();

const tabLabels = ref(slots.default?.().map(tab => tab.props?.label));

const props = defineProps({
  barClass: {
    type: String,
    default: '',
  },
});

const goto = (index: number) => {
  if (!contentEl.value.children[index]) {
    return;
  }

  activeIndex.value = index;
  dropdownOpen.value = false;

  nextTick(() => moveLine());

  nextTick(() => {
    window.scrollTo({
      top: window.scrollY + contentEl.value.children[index].getBoundingClientRect().top - 100,
      left: 0,
      behavior: 'smooth',
    });
  });
};

const toggleDropdown = () => {
  dropdownOpen.value = !dropdownOpen.value;
};

const { y } = useWindowScroll();

onClickOutside(dropdownRef, () => {
  dropdownOpen.value = false;
});

const moveLine = () => {
  const bar = navigatorEl.value;
  const line = lineEl.value;
  const item = listEl.value?.children[activeIndex.value] as HTMLElement | undefined;

  if (!bar || !line || !item) {
    return;
  }

  const barRect = bar.getBoundingClientRect();
  const itemRect = item.getBoundingClientRect();

  line.style.top = `${itemRect.bottom - barRect.top - 2}px`;
  line.style.width = `${itemRect.width}px`;
  line.style.transform = `translateX(${itemRect.left - barRect.left}px)`;
};

onMounted(() => {
  moveLine();

  lineEl.value.style.opacity = '1';
  lineEl.value.style.height = '2px';

  const offsetTop = navigatorEl.value?.offsetTop ?? 0;

  watchThrottled(y, (value) => {
    fixed.value = value > offsetTop;
  }, {
    throttle: 100,
  });

  useResizeObserver(navigatorEl, moveLine);

  scroller.setup({
    step: Array.from(contentEl.value.children),
    offset: 0.3,
  }).onStepEnter(({ index }) => {
    activeIndex.value = index;

    moveLine();
  });
});

onUnmounted(() => {
  scroller.destroy();
});

defineExpose({
  goto,
});
</script>

<template>
  <div class="float-navigator">
    <div class="float-navigator__bar z-60" :class="[ fixed ? 'fixed' : 'relative', props.barClass ]" ref="navigatorEl">
      <!-- PC: 水平 tab 列表 -->
      <ul class="m-0 h-full flex list-none justify-between p-0 <md:hidden" ref="listEl">
        <li v-for="(item, index) in tabLabels" :key="index" class="h-full flex flex-grow basis-0 cursor-pointer items-center justify-center text-center" @click="goto(index)">
          {{ item }}
        </li>
      </ul>

      <div class="float-navigator__line absolute left-0 transition-all duration-300 <md:hidden" ref="lineEl"></div>

      <!-- 移动端: 通栏下拉导航 -->
      <div class="float-navigator__dropdown relative hidden h-full <md:block" ref="dropdownRef">
        <div class="h-full flex cursor-pointer items-center gap-8 px-16 active:bg-gray-50" @click="toggleDropdown">
          <svg class="flex-shrink-0 text-brand-primary" width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M2 4h12M2 8h8M2 12h10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
          </svg>
          <span class="flex-1 truncate text-15 text-brand-normal font-medium">{{ tabLabels?.[activeIndex] }}</span>
          <svg class="flex-shrink-0 text-brand-gray transition-transform duration-200" :class="{ 'rotate-180': dropdownOpen }" width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M2 4L6 8L10 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </div>

        <Transition name="dropdown">
          <ul v-show="dropdownOpen" class="dropdown-list absolute left-0 right-0 top-full z-50 m-0 list-none bg-white p-0">
            <li
              v-for="(item, index) in tabLabels"
              :key="index"
              class="relative cursor-pointer px-16 py-12 text-14 transition-colors active:bg-gray-50"
              :class="index === activeIndex ? 'text-brand-primary font-medium bg-brand-primary/5' : 'text-brand-normal'"
              @click="goto(index)"
            >
              <span v-if="index === activeIndex" class="absolute bottom-10 left-0 top-10 w-3 rounded-r bg-brand-primary"></span>
              {{ item }}
            </li>
          </ul>
        </Transition>
      </div>
    </div>

    <div class="float-navigator__content" ref="contentEl">
      <slot />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.float-navigator {
  $navigator-height: 64px;

  .float-navigator__bar {
    top: 0;
    right: 0;
    left: 0;
    height: $navigator-height;
    margin-top: calc(-1 * $navigator-height / 2);
    background-color: #fff;
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.06);
    transition: width, max-width .2s;

    @media (max-width: 767.9px) {
      position: sticky;
      top: 0;
      z-index: 60;
      width: 100vw;
      max-width: none;
      height: 48px;
      margin-top: 0;
      margin-right: calc(50% - 50vw);
      margin-left: calc(50% - 50vw);
    }

    &.fixed {
      width: 100%;
      max-width: 100%;
      margin-top: 0;

      @media (max-width: 767.9px) {
        width: 100vw;
        max-width: none;
        margin-right: calc(50% - 50vw);
        margin-left: calc(50% - 50vw);
      }
    }

    li {
      font-size: 16px;

      &:hover {
        color: #1180ff;
      }
    }

    .float-navigator__line {
      height: 0;
      background-color: #1180ff;
      opacity: 0;
    }
  }

  .float-navigator__dropdown {
    .dropdown-list {
      box-shadow: 0 8px 16px 0 rgba(0, 0, 0, 0.08);
    }
  }

  .float-navigator__content {
    margin-top: calc(-1 * $navigator-height / 2);

    @media (max-width: 767.9px) {
      margin-top: 0;
    }
  }
}

// 下拉动画
.dropdown-enter-active,
.dropdown-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
  transform: translateY(-8px);
  opacity: 0;
}
</style>
